import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Picks a page size that fills the list area on any screen without stretching rows.
 *
 * The page must give the hook an element whose height is exactly the space the list
 * may use (a `flex: 1; min-height: 0` child) and keep that element mounted while
 * loading, so the size is known before the first request. The element should scroll,
 * so an item height that is slightly off costs a few pixels of scroll instead of
 * clipping rows.
 *
 * `getLayout(area)` reads the metrics of the rendered list and returns
 *   { itemHeight, overhead = 0, gap = 0, columns = 1 }
 * or null while the list is not on screen (loading, empty, error). In that case the
 * last measured metrics are reused, falling back to `fallbackLayout` for the very
 * first request — reusing them matters, because measuring a placeholder differently
 * than the list would make the page size flip back and forth on every load.
 *
 * Item heights should therefore also be fixed in CSS, so a measurement cannot drift
 * with the data. Pass the loading flag as `revision` to re-read them after a render.
 */
export const useFittingPageSize = ({
  areaRef,
  getLayout,
  fallbackLayout,
  revision,
}) => {
  const [perPage, setPerPage] = useState(0);
  const getLayoutRef = useRef(getLayout);
  const lastLayoutRef = useRef(null);
  getLayoutRef.current = getLayout;

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return undefined;

    const measure = () => {
      const height = area.clientHeight;
      if (!height) return;

      const measured = getLayoutRef.current(area);
      if (measured) lastLayoutRef.current = measured;

      const layout = measured || lastLayoutRef.current || fallbackLayout;
      const { itemHeight, overhead = 0, gap = 0, columns = 1 } = layout || {};
      if (!itemHeight) return;

      const rows = Math.max(1, Math.floor((height - overhead + gap) / (itemHeight + gap)));
      const next = rows * columns;
      setPerPage((current) => (current === next ? current : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaRef, revision]);

  return perPage;
};

/**
 * Keeps the first item of the current page in view when the page size changes,
 * instead of sending the user back to page one on every window resize.
 */
export const useKeepFirstItemVisible = (perPage, setPage) => {
  const previousRef = useRef(perPage);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = perPage;
    if (!perPage || !previous) return;
    setPage((current) => Math.floor(((current - 1) * previous) / perPage) + 1);
  }, [perPage, setPage]);
};

/** Counts the columns an already rendered CSS grid is using. */
export const gridColumnCount = (grid) => {
  const template = window.getComputedStyle(grid).gridTemplateColumns;
  return template && template !== 'none' ? template.split(' ').length : 0;
};

/** Tallest rendered item, so a page size never clips the roomiest one. */
export const tallestItemHeight = (items) => (
  Array.from(items || []).reduce(
    (tallest, item) => Math.max(tallest, item.getBoundingClientRect().height),
    0
  )
);
